// Push Notifications Service
// Handles web push notification subscriptions and management

import { supabase } from './supabase';
import { deepLinkNavigate } from './deepLinkNavigation';

// VAPID keys - these will be generated and stored securely
const VAPID_PUBLIC_KEY = 'BAzHa8v3mcXV1Un80OdmQvwx9HTe7wqZbRaN0abztWPkwRYm5Lw75THYAFCknKKX_weuib-JKiwSRmDzrGVmii8';

export interface PushSubscription {
  id?: string;
  user_id: string;
  business_id: string;
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  user_agent?: string;
  created_at?: string;
  updated_at?: string;
  is_active: boolean;
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  url?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  vibrate?: number[];
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  data?: Record<string, any>;
}

export class PushNotificationService {
  private static instance: PushNotificationService;
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: globalThis.PushSubscription | null = null;
  private vapidPublicKey: string = VAPID_PUBLIC_KEY;
  private isRestoring: boolean = false; // Lock to prevent concurrent restorations

  private constructor() {
    this.initializeServiceWorker();
  }

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  /**
   * Initialize the service worker for push notifications
   * Uses existing service worker registration from main.tsx
   */
  private async initializeServiceWorker(): Promise<void> {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push notifications not supported in this browser');
        return;
      }

      // Get existing service worker registration (registered in main.tsx)
      this.registration = await navigator.serviceWorker.ready;

      if (!this.registration) {
        console.warn('Service worker not ready yet, push notifications may not work');
        return;
      }

      console.log('Push service worker ready:', this.registration);

      // Send VAPID key to service worker (critical for subscription restoration)
      if (this.registration.active) {
        this.registration.active.postMessage({
          type: 'SET_VAPID_KEY',
          vapidKey: this.vapidPublicKey
        });
      } else if (this.registration.waiting) {
        // If active isn't ready, send to waiting worker
        this.registration.waiting.postMessage({
          type: 'SET_VAPID_KEY',
          vapidKey: this.vapidPublicKey
        });
      } else if (this.registration.installing) {
        // If installing, send to installing worker
        this.registration.installing.postMessage({
          type: 'SET_VAPID_KEY',
          vapidKey: this.vapidPublicKey
        });
      }

      // Listen for service worker updates
      this.registration.addEventListener('updatefound', () => {
        console.log('Service worker update found');
        if (this.registration) {
          const newWorker = this.registration.installing;
          if (newWorker) {
            // Send VAPID key to new worker
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                newWorker.postMessage({
                  type: 'SET_VAPID_KEY',
                  vapidKey: this.vapidPublicKey
                });
                // Restore subscription after service worker update
                this.restoreSubscriptionAfterUpdate();
              }
            });
          }
        }
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event);
      });
      
      // NOTE: NO automatic restoration
      // Subscriptions are ONLY created when user explicitly subscribes
      // This prevents ghost subscriptions and unwanted notifications
      console.log('✅ Push service initialized - user must explicitly subscribe to enable notifications');

    } catch (error) {
      console.error('Failed to register push service worker:', error);
    }
  }

  /**
   * Handle messages from the service worker
   */
  private handleServiceWorkerMessage(event: MessageEvent): void {
    console.log('Message from service worker:', event.data);

    if (event.data?.type === 'NOTIFICATION_CLICK') {
      // Handle navigation when notification is clicked
      const url = event.data.url;
      const data = event.data.data;

      // Navigate in-app via React Router (avoids a hard reload that re-boots the SPA
      // into a different state and breaks the post-open call flow).
      if (url) {
        deepLinkNavigate(url);
      }

      // Emit custom event for the app to handle
      window.dispatchEvent(new CustomEvent('notificationClick', {
        detail: { url, data }
      }));
    } else if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      // Handle push subscription change from service worker
      console.log('Push subscription changed, updating database...');
      this.handleSubscriptionChange(event.data.subscription);
    } else if (event.data?.type === 'REQUEST_VAPID_KEY') {
      // Service worker requesting VAPID key
      if (this.registration?.active) {
        this.registration.active.postMessage({
          type: 'SET_VAPID_KEY',
          vapidKey: this.vapidPublicKey
        });
      }
    }
  }

  /**
   * Handle push subscription change (e.g., after service worker update)
   */
  private async handleSubscriptionChange(subscriptionData: any): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('User not authenticated, cannot update subscription');
        return;
      }

      const businessId = await this.getBusinessIdForUser(user.id);
      if (!businessId) {
        console.warn('No business found for subscription update');
        return;
      }

      // Update or create subscription in database
      const subscriptionRecord: PushSubscription = {
        user_id: user.id,
        business_id: businessId,
        endpoint: subscriptionData.endpoint,
        p256dh_key: subscriptionData.keys.p256dh,
        auth_key: subscriptionData.keys.auth,
        user_agent: navigator.userAgent,
        is_active: true
      };

      // If old endpoint exists, mark it as inactive first
      if (subscriptionData.oldEndpoint) {
        await supabase
          .from('push_notification_subscriptions')
          .update({ is_active: false })
          .eq('endpoint', subscriptionData.oldEndpoint);
      }

      // Check if subscription exists, then insert or update
      const { data: existing } = await supabase
        .from('push_notification_subscriptions')
        .select('id')
        .eq('user_id', subscriptionRecord.user_id)
        .eq('endpoint', subscriptionRecord.endpoint)
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('push_notification_subscriptions')
          .update({ ...subscriptionRecord, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        error = result.error;
      } else {
        const result = await supabase
          .from('push_notification_subscriptions')
          .insert(subscriptionRecord);
        error = result.error;
      }

      if (error) {
        console.error('Error updating subscription after change:', error);
      } else {
        console.log('✅ Subscription updated in database after change');
      }
    } catch (error) {
      console.error('Error handling subscription change:', error);
    }
  }

  /**
   * Restore subscription info saved by the service worker when no clients were open.
   */
  private async restoreSubscriptionBackupIfNeeded(): Promise<void> {
    try {
      if (!('caches' in window)) {
        return;
      }

      const cache = await caches.open('push-subscription-backup');
      const response = await cache.match('/subscription-backup.json');
      if (!response) {
        return;
      }

      const backup = await response.json();
      if (!backup?.subscription?.endpoint || !backup?.subscription?.keys?.p256dh || !backup?.subscription?.keys?.auth) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      const businessId = await this.getBusinessIdForUser(user.id);
      if (!businessId) {
        console.warn('No business found for subscription backup restore');
        return;
      }

      const subscriptionRecord: PushSubscription = {
        user_id: user.id,
        business_id: businessId,
        endpoint: backup.subscription.endpoint,
        p256dh_key: backup.subscription.keys.p256dh,
        auth_key: backup.subscription.keys.auth,
        user_agent: navigator.userAgent,
        is_active: true
      };

      // Check if subscription exists, then insert or update
      const { data: existing } = await supabase
        .from('push_notification_subscriptions')
        .select('id')
        .eq('user_id', subscriptionRecord.user_id)
        .eq('endpoint', subscriptionRecord.endpoint)
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('push_notification_subscriptions')
          .update({ ...subscriptionRecord, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        error = result.error;
      } else {
        const result = await supabase
          .from('push_notification_subscriptions')
          .insert(subscriptionRecord);
        error = result.error;
      }

      if (!error) {
        await cache.delete('/subscription-backup.json');
        console.log('✅ Restored subscription from backup cache');
      } else {
        console.warn('Error restoring subscription from backup cache:', error);
      }
    } catch (error) {
      console.warn('Error checking subscription backup cache:', error);
    }
  }

  /**
   * Resolve a business ID for the user (supports owners + members).
   */
  private async getBusinessIdForUser(userId: string): Promise<string | null> {
    try {
      const { data: membership } = await supabase
        .from('business_users')
        .select('business_id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();

      if (membership?.business_id) {
        return membership.business_id;
      }

      // Fallback: check resources table for resource accounts
      const { data: resource } = await supabase
        .from('resources')
        .select('business_id')
        .eq('auth_user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (resource?.business_id) {
        return resource.business_id;
      }

      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('auth_user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      return business?.id ?? null;
    } catch (error) {
      console.warn('Error resolving business ID for user:', error);
      return null;
    }
  }

  /**
   * Restore subscription if it exists in browser but not in database
   * Enhanced with retry logic and better error handling
   * Public method so it can be called from hooks and other services
   */
  public async restoreSubscriptionIfNeeded(retryCount: number = 0): Promise<void> {
    const MAX_RETRIES = 3; // Reduced from 5
    const RETRY_DELAY = 3000; // Increased to 3 seconds

    // Lock to prevent concurrent restorations
    if (this.isRestoring) {
      console.log('⏸️ Restoration already in progress, skipping');
      return;
    }

    this.isRestoring = true;

    try {
      if (!this.registration) {
        if (retryCount < MAX_RETRIES) {
          console.log(`🔄 Service worker not ready, retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
          this.isRestoring = false;
          setTimeout(() => this.restoreSubscriptionIfNeeded(retryCount + 1), RETRY_DELAY);
        } else {
          this.isRestoring = false;
        }
        return;
      }

      const browserSubscription = await this.registration.pushManager.getSubscription();
      if (!browserSubscription) {
        console.log('No browser subscription found, nothing to restore');
        this.isRestoring = false;
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (retryCount < MAX_RETRIES) {
          console.log(`🔄 User not authenticated yet, retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
          this.isRestoring = false;
          setTimeout(() => this.restoreSubscriptionIfNeeded(retryCount + 1), RETRY_DELAY);
        } else {
          console.log('User not authenticated after retries, skipping subscription restoration');
          this.isRestoring = false;
        }
        return;
      }

      // Check if subscription exists in database with this exact endpoint
      const { data: dbSubscription } = await supabase
        .from('push_notification_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('endpoint', browserSubscription.endpoint)
        .maybeSingle();

      if (dbSubscription) {
        // If subscription exists but is inactive, reactivate it
        if (!dbSubscription.is_active) {
          console.log('🔄 Reactivating existing subscription...');
          const { error: updateError } = await supabase
            .from('push_notification_subscriptions')
            .update({ is_active: true, updated_at: new Date().toISOString() })
            .eq('id', dbSubscription.id);
          
          if (updateError) {
            console.error('Error reactivating subscription:', updateError);
          } else {
            console.log('✅ Subscription reactivated in database');
          }
        } else {
          console.log('✅ Subscription already exists and is active in database');
        }
        this.isRestoring = false;
        return;
      }

      // Check if user has ANY existing subscription (with different endpoint)
      // If so, delete the old one before creating new (endpoint changed)
      const { data: existingSubscriptions } = await supabase
        .from('push_notification_subscriptions')
        .select('id, endpoint')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (existingSubscriptions && existingSubscriptions.length > 0) {
        console.log(`🧹 Cleaning up ${existingSubscriptions.length} old subscription(s) with different endpoint(s)...`);
        for (const oldSub of existingSubscriptions) {
          await supabase
            .from('push_notification_subscriptions')
            .delete()
            .eq('id', oldSub.id);
          console.log(`🗑️ Deleted old subscription with endpoint: ${oldSub.endpoint.substring(0, 50)}...`);
        }
      }

      // Subscription exists in browser but not in database - create it
      console.log('🔄 Creating subscription in database...');
      
      const businessId = await this.getBusinessIdForUser(user.id);
      if (!businessId) {
        console.warn('No business found, cannot create subscription');
        this.isRestoring = false;
        return;
      }

      const subscriptionRecord: PushSubscription = {
        user_id: user.id,
        business_id: businessId,
        endpoint: browserSubscription.endpoint,
        p256dh_key: btoa(String.fromCharCode(...new Uint8Array(browserSubscription.getKey('p256dh')!))),
        auth_key: btoa(String.fromCharCode(...new Uint8Array(browserSubscription.getKey('auth')!))),
        user_agent: navigator.userAgent,
        is_active: true
      };

      // Check if subscription exists, then insert or update
      const { data: existing } = await supabase
        .from('push_notification_subscriptions')
        .select('id')
        .eq('user_id', subscriptionRecord.user_id)
        .eq('endpoint', subscriptionRecord.endpoint)
        .maybeSingle();

      let error;
      let insertData;
      if (existing) {
        const result = await supabase
          .from('push_notification_subscriptions')
          .update({ ...subscriptionRecord, updated_at: new Date().toISOString() })
          .eq('id', existing.id)
          .select();
        error = result.error;
        insertData = result.data;
      } else {
        const result = await supabase
          .from('push_notification_subscriptions')
          .insert(subscriptionRecord)
          .select();
        error = result.error;
        insertData = result.data;
      }

      if (error) {
        console.error('❌ Error creating subscription:', error);
        if (retryCount < MAX_RETRIES) {
          console.log(`🔄 Retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
          this.isRestoring = false;
          setTimeout(() => this.restoreSubscriptionIfNeeded(retryCount + 1), RETRY_DELAY);
          return;
        } else {
          console.error('❌ Failed after all retries');
        }
      } else {
        console.log('✅ Subscription saved to database');
        if (insertData && insertData.length > 0) {
          console.log('✅ Subscription ID:', insertData[0].id);
        }
      }
    } catch (error) {
      console.error('Error in subscription restoration:', error);
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
        this.isRestoring = false;
        setTimeout(() => this.restoreSubscriptionIfNeeded(retryCount + 1), RETRY_DELAY);
        return;
      }
    }
    
    this.isRestoring = false;
  }

  /**
   * Handle service worker update
   * NOTE: No auto-restoration - user must explicitly subscribe
   */
  private async restoreSubscriptionAfterUpdate(): Promise<void> {
    console.log('ℹ️ Service worker updated - subscription status unchanged');
    // Do NOT auto-restore - if user was subscribed, they remain subscribed
    // The browser maintains the subscription across SW updates
  }

  /**
   * Check if push notifications are supported
   */
  public isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current notification permission status
   */
  public getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  public async requestPermission(): Promise<NotificationPermission> {
    try {
      if (!this.isSupported()) {
        throw new Error('Push notifications not supported');
      }

      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Subscribe to push notifications
   */
  public async subscribe(userId: string, businessId: string): Promise<PushSubscription | null> {
    try {
      if (!userId || !businessId) {
        throw new Error('Missing user or business ID');
      }

      // Ensure service worker registration is ready
      if (!this.registration) {
        console.log('⚠️ Service worker registration not ready, waiting for it...');
        // Try to get registration
        try {
          this.registration = await navigator.serviceWorker.ready;
        } catch (error) {
          // If ready fails, try getRegistration
          this.registration = await navigator.serviceWorker.getRegistration() || null;
        }
        
        if (!this.registration) {
          throw new Error('Service worker not ready. Please wait a moment and try again, or refresh the page.');
        }
      }

      // Ensure service worker is active
      if (!this.registration.active) {
        console.log('⚠️ Service worker not active, waiting for activation...');
        // Wait for service worker to be ready
        this.registration = await navigator.serviceWorker.ready;
        
        if (!this.registration.active) {
          throw new Error('Service worker is not active. Please refresh the page and try again.');
        }
      }

      // Check current permission - don't request again if already granted
      // iOS requires permission requests to come from direct user gesture
      // If we already have permission, don't re-request (it will fail on iOS)
      const currentPermission = this.getPermissionStatus();
      if (currentPermission !== 'granted') {
        // Only request if not already granted
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          throw new Error('Notification permission not granted');
        }
      }
      console.log('✅ Notification permission is granted');

      // Create push subscription
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey) as BufferSource
      });

      console.log('Push subscription created:', this.subscription);

      // Convert subscription to our format
      const subscriptionData: PushSubscription = {
        user_id: userId,
        business_id: businessId,
        endpoint: this.subscription.endpoint,
        p256dh_key: btoa(String.fromCharCode(...new Uint8Array(this.subscription.getKey('p256dh')!))),
        auth_key: btoa(String.fromCharCode(...new Uint8Array(this.subscription.getKey('auth')!))),
        user_agent: navigator.userAgent,
        is_active: true
      };

      // Save subscription to database. One device = one row: lookup by endpoint only, then update or insert.
      console.log('📝 Saving subscription to database...');
      console.log('📝 User ID:', userId);
      console.log('📝 Business ID:', businessId);
      console.log('📝 Endpoint:', this.subscription.endpoint.substring(0, 50) + '...');

      const { data: existing, error: checkError } = await supabase
        .from('push_notification_subscriptions')
        .select('id')
        .eq('endpoint', this.subscription.endpoint)
        .maybeSingle();

      if (checkError) {
        console.error('❌ Error checking for existing subscription:', checkError);
      }

      let data;
      let error;

      if (existing) {
        // Same device (endpoint) already has a row - update to current user/business so one device = one entry
        console.log('📝 Updating existing subscription for this device:', existing.id);
        const result = await supabase
          .from('push_notification_subscriptions')
          .update({
            ...subscriptionData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();
        data = result.data;
        error = result.error;
        console.log('📝 Update result:', { data, error });
      } else {
        // New device - insert
        console.log('📝 Inserting new subscription...');
        const result = await supabase
          .from('push_notification_subscriptions')
          .insert(subscriptionData)
          .select()
          .single();
        data = result.data;
        error = result.error;
        console.log('📝 Insert result:', { data, error });
      }

      if (error) {
        console.error('❌ Error saving push subscription:', error);
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.details);
        throw new Error(`Failed to save subscription: ${error.message}`);
      }

      console.log('✅ Push subscription saved to database:', data);
      return data;

    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications on THIS DEVICE ONLY
   * Multi-device safe: only removes the current device's subscription
   * Other devices logged into the same account keep their subscriptions
   */
  public async unsubscribe(userId: string): Promise<boolean> {
    try {
      // Get the browser's push subscription for THIS device
      const registration = await navigator.serviceWorker.getRegistration();
      const browserSubscription = await registration?.pushManager.getSubscription();
      const endpoint = browserSubscription?.endpoint;

      // CRITICAL: Actually unsubscribe from the browser's Push Manager
      // This removes the subscription from the browser, preventing auto-restoration
      if (browserSubscription) {
        console.log('🗑️ Unsubscribing from browser Push Manager...');
        await browserSubscription.unsubscribe();
        console.log('✅ Successfully unsubscribed from browser Push Manager');
      }

      // Also clear the local reference
      if (this.subscription) {
        this.subscription = null;
      }

      // Remove only THIS DEVICE's subscription from database
      // Uses endpoint as device identifier - each device has unique endpoint
      // This preserves subscriptions from other devices logged into same account
      if (endpoint) {
        const { error } = await supabase
          .from('push_notification_subscriptions')
          .delete()
          .eq('user_id', userId)
          .eq('endpoint', endpoint);

        if (error) {
          console.error('Error removing push subscription from database:', error);
        } else {
          console.log(`✅ Successfully removed THIS DEVICE's subscription from database`);
          console.log(`   Endpoint: ${endpoint.substring(0, 50)}...`);
        }
      } else {
        // No browser subscription found - clean up any orphaned entries for this user
        // that might have been left from previous sessions on THIS device
        console.log('⚠️ No browser subscription found - this device may already be unsubscribed');
      }

      console.log('✅ Unsubscribed from push notifications on this device');
      return true;

    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications on ALL DEVICES
   * Use this for complete cleanup (e.g., account deletion, security reset)
   * WARNING: This will stop notifications on all devices for this user
   */
  public async unsubscribeAllDevices(userId: string): Promise<boolean> {
    try {
      // First unsubscribe THIS device from browser
      const registration = await navigator.serviceWorker.getRegistration();
      const browserSubscription = await registration?.pushManager.getSubscription();
      
      if (browserSubscription) {
        console.log('🗑️ Unsubscribing this device from browser Push Manager...');
        await browserSubscription.unsubscribe();
      }

      // Clear local reference
      if (this.subscription) {
        this.subscription = null;
      }

      // Remove ALL subscriptions for this user from database
      const { data: deletedSubs, error } = await supabase
        .from('push_notification_subscriptions')
        .delete()
        .eq('user_id', userId)
        .select('id, endpoint');

      if (error) {
        console.error('Error removing all push subscriptions from database:', error);
        return false;
      }

      const count = deletedSubs?.length || 0;
      console.log(`✅ Removed ${count} subscription(s) across ALL devices for user ${userId}`);
      
      return true;

    } catch (error) {
      console.error('Error unsubscribing all devices:', error);
      return false;
    }
  }

  /**
   * Get current push subscription for THIS DEVICE ONLY
   * Checks browser subscription first (device-specific), then verifies in database
   * CRITICAL: This is device-specific - only returns subscription if THIS device has it
   * 
   * Device-specificity is guaranteed by:
   * 1. Checking browser's PushManager.getSubscription() FIRST (device-specific API)
   * 2. Matching endpoint in database (each device has unique endpoint)
   * 3. Verifying user_id matches current authenticated user
   */
  public async getSubscription(userId?: string): Promise<PushSubscription | null> {
    try {
      const resolvedUserId = userId ?? (await supabase.auth.getUser()).data.user?.id;
      if (!resolvedUserId) {
        return null;
      }
      // CRITICAL STEP 1: Check browser subscription FIRST - this is device-specific
      // PushManager.getSubscription() only returns THIS device's subscription
      // Other devices have different endpoints, so they won't match
      if (!this.registration) {
        console.log('🔍 Device check: No service worker registration - no subscription on THIS device');
        return null;
      }

      const browserSubscription = await this.registration.pushManager.getSubscription();
      if (!browserSubscription) {
        console.log('🔍 Device check: No browser subscription found on THIS device');
        return null; // This device has no subscription - device-specific check passed
      }

      console.log('🔍 Device check: Browser has subscription on THIS device, endpoint:', browserSubscription.endpoint.substring(0, 50) + '...');

      // CRITICAL STEP 2: Verify this subscription exists in database for THIS user
      // Match by endpoint (device-specific) AND user_id (user-specific)
      // This ensures we're not showing subscriptions from other devices or users
      const { data: dbSubscription, error } = await supabase
        .from('push_notification_subscriptions')
        .select('*')
        .eq('user_id', resolvedUserId) // User-specific check
        .eq('endpoint', browserSubscription.endpoint) // Device-specific check (endpoint is unique per device)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // Not found is ok
        console.error('Error fetching push subscription:', error);
        return null;
      }

      // If browser has subscription but database doesn't, return null
      // Do NOT auto-restore - let the user explicitly subscribe
      if (!dbSubscription) {
        console.log('🔍 Device check: Browser has subscription but database doesn\'t - subscription not active');
        return null;
      }

      // If subscription exists but is inactive, return null (user unsubscribed)
      // Do NOT auto-reactivate - user explicitly disabled notifications
      if (!dbSubscription.is_active) {
        console.log('🔍 Device check: Subscription exists but is inactive - not subscribed');
        return null;
      }

      // Return database subscription (matches THIS device's browser subscription)
      console.log('✅ Device check: Subscription verified for THIS device');
      return dbSubscription;
    } catch (error) {
      console.error('Error getting push subscription:', error);
      return null;
    }
  }

  /**
   * Get subscription status for THIS DEVICE ONLY (simpler check)
   * Returns true only if THIS device has an active browser subscription
   */
  public async hasSubscriptionOnThisDevice(): Promise<boolean> {
    try {
      if (!this.registration) {
        return false;
      }

      const browserSubscription = await this.registration.pushManager.getSubscription();
      return !!browserSubscription; // True only if THIS device has a subscription
    } catch (error) {
      console.error('Error checking device subscription:', error);
      return false;
    }
  }

  /**
   * Test push notification (for development)
   */
  public async testNotification(): Promise<boolean> {
    try {
      console.log('🔔 [TEST] Testing notification...');
      console.log('🔔 [TEST] Is supported:', this.isSupported());
      console.log('🔔 [TEST] Permission status:', this.getPermissionStatus());

      if (!this.isSupported()) {
        console.error('🔔 [TEST] Push notifications not supported');
        return false;
      }

      if (this.getPermissionStatus() !== 'granted') {
        console.error('🔔 [TEST] Permission not granted:', this.getPermissionStatus());
        return false;
      }

      console.log('🔔 [TEST] Showing test notification...');

      // Show a local test notification
      const notificationOptions: NotificationOptions & { vibrate?: number[]; timestamp?: number } = {
        body: '✅ SUCCESS! Push notifications are working perfectly! This should be very visible!',
        icon: '/favicon.ico',
        tag: `test-notification-${Date.now()}`,
        requireInteraction: true, // Keeps notification visible until user interacts
        silent: false, // Make sure it's not silent
        vibrate: [200, 100, 200], // Vibration pattern (mobile) - not in TS types but supported by browsers
        timestamp: Date.now() // Not in TS types but supported by browsers
      };
      
      const notification = new Notification('🚨 URGENT: Booking App Test 🚨', notificationOptions);

      // Log when notification is shown
      notification.onshow = () => {
        console.log('🔔 [TEST] Notification is now visible on screen');
      };

      // Log when notification is clicked
      notification.onclick = () => {
        console.log('🔔 [TEST] Notification clicked!');
        notification.close();
      };

      // Log any errors
      notification.onerror = (error) => {
        console.error('🔔 [TEST] Notification error:', error);
      };

      console.log('🔔 [TEST] Test notification created successfully');
      return true;
    } catch (error) {
      console.error('🔔 [TEST] Error showing test notification:', error);
      return false;
    }
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Check if THIS DEVICE has an active subscription
   * Device-specific check - only returns true if THIS device has a subscription
   * Note: userId parameter is kept for API compatibility but device check doesn't need it
   */
  public async hasActiveSubscription(_userId: string): Promise<boolean> {
    // Use the device-specific check method (doesn't need userId - checks THIS device only)
    return await this.hasSubscriptionOnThisDevice();
  }

  /**
   * Update subscription status
   */
  public async updateSubscriptionStatus(userId: string, isActive: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('push_notification_subscriptions')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('user_id', userId);

      if (error) {
        console.error('Error updating subscription status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating subscription status:', error);
      return false;
    }
  }

  /**
   * Validate subscription health and restore if needed
   * Checks if browser subscription matches database subscription
   * Automatically restores if there's a mismatch
   */
  public async validateAndRestoreSubscription(): Promise<void> {
    try {
      if (!this.registration) {
        console.log('🔄 Service worker not ready for validation');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('🔄 User not authenticated for validation');
        return;
      }

      // Get browser subscription
      const browserSubscription = await this.registration.pushManager.getSubscription();
      
      // Get database subscriptions for this user
      const { data: dbSubscriptions } = await supabase
        .from('push_notification_subscriptions')
        .select('*')
        .eq('user_id', user.id);

      if (!browserSubscription) {
        // Browser has no subscription - check if database has inactive ones
        if (dbSubscriptions && dbSubscriptions.length > 0) {
          console.log('⚠️ Browser subscription missing but database has records - user may have unsubscribed');
        }
        return;
      }

      // Check if browser subscription exists in database
      const matchingDbSub = dbSubscriptions?.find(
        sub => sub.endpoint === browserSubscription.endpoint
      );

      if (!matchingDbSub) {
        // Browser has subscription but database doesn't
        // Do NOT auto-restore - user must explicitly subscribe
        console.log('ℹ️ Validation: Browser has subscription but not in database - user not subscribed');
      } else if (!matchingDbSub.is_active) {
        // Database subscription exists but is inactive
        // Do NOT auto-reactivate - user explicitly unsubscribed
        console.log('ℹ️ Validation: Subscription exists but inactive - user unsubscribed');
      } else {
        // Subscription is healthy
        console.log('✅ Subscription validation passed');
      }

      // CRITICAL: Only mark subscriptions as inactive if they're truly orphaned
      // An "orphaned" subscription is one that:
      // 1. Exists in database with is_active=true
      // 2. Does NOT exist in browser (endpoint doesn't match)
      // 3. Is for a DIFFERENT device (different endpoint)
      // 
      // IMPORTANT: We should NOT deactivate subscriptions just because they're on other devices
      // Each device has its own subscription, and we want to preserve all of them
      // Only deactivate if we're CERTAIN the subscription is invalid (e.g., user unsubscribed on that device)
      //
      // For now, we'll be conservative and NOT auto-deactivate orphaned subscriptions
      // The server-side push function will handle deactivating truly invalid subscriptions (410/404 errors)
      // This prevents accidentally deactivating valid subscriptions from other devices
      console.log('✅ Validation complete - preserving all device subscriptions (server handles invalid ones)');
    } catch (error) {
      console.error('Error validating subscription:', error);
    }
  }

  /**
   * Backwards-compatible wrapper: create subscription using current user.
   */
  public async createSubscription(businessId: string): Promise<PushSubscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return this.subscribe(userId, businessId);
  }

  /**
   * Backwards-compatible wrapper: remove subscription for current user/device.
   */
  public async removeSubscription(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    await this.unsubscribe(userId);
  }

  /**
   * Backwards-compatible wrapper: test notification.
   */
  public async sendTestNotification(_businessId: string): Promise<void> {
    const success = await this.testNotification();
    if (!success) {
      throw new Error('Test notification failed');
    }
  }
}

// Export singleton instance
export const pushNotificationService = PushNotificationService.getInstance();
